import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import {
  resolveConversationDestination,
  type ConversationDestinationReaders,
} from "messaging/conversations/conversation-destination";
import {
  conversationRecordIdParameter,
  conversationRecordParameter,
  forumDialogPath,
  isConversationRecord,
} from "messaging/conversations/conversation-links";
import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import Session from "users/__session/session";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import Proposal from "work/proposals/proposal";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import WorkAgreement from "work/work-agreements/work-agreement";

const Waiting = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(6, 2),
}));

/**
 * Reads the record through the app's one API instance, so the page the reader is sent to finds it
 * already in the cache. Each read lets go of its cache entry once answered; the page that opens next
 * holds its own.
 *
 * Every read goes to the server, never to the cache. The answer decides whether the reader may open
 * the record at all, and a cached copy from before they were taken off the team would send them to a
 * page that then refuses them, rather than to the chat dialog.
 */
function readersFor(dispatch: ReturnType<typeof useAttorneyHubDispatch>, session: Session): ConversationDestinationReaders {
  return {
    readWorkAgreement: async (workAgreementId) => {
      const request = dispatch(
        workAgreementsApi.endpoints.getWorkAgreementById.initiate({ workAgreementId, session }, { forceRefetch: true }),
      );
      try {
        const json = await request.unwrap();
        return json ? WorkAgreement.fromJSON(json) : null;
      } finally {
        request.unsubscribe();
      }
    },
    readProposal: async (proposalId) => {
      const request = dispatch(proposalsApi.endpoints.getProposalById.initiate({ proposalId, session }, { forceRefetch: true }));
      try {
        const json = await request.unwrap();
        return session.user ? Proposal.fromJSON({ json, currentUser: session.user }) : null;
      } finally {
        request.unsubscribe();
      }
    },
  };
}

/**
 * Where a message notification about a work agreement's or a proposal's conversation lands (#5826).
 *
 * It sends the reader on to the record's page with the pane open on the conversation, or to the
 * generic chat dialog when they cannot open the record. It replaces itself in the history, so Back
 * returns to wherever the reader clicked the notification.
 */
export default function ConversationDestinationPage() {
  const { forumId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const record = search.get(conversationRecordParameter);
  const recordId = search.get(conversationRecordIdParameter);

  // One read per request. The session object can be replaced while the read is in flight, and keyed
  // on it alone the effect read the record twice in the browser. The request is the forum and the
  // record, so that is what the guard is keyed on.
  const startedRequest = useRef<string | undefined>(undefined);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!forumId) {
      navigate("/communications", { replace: true });
      return;
    }
    if (!isConversationRecord(record) || !recordId) {
      navigate(forumDialogPath(forumId), { replace: true });
      return;
    }

    const request = `${forumId}|${record}|${recordId}`;
    if (startedRequest.current === request) return;
    startedRequest.current = request;
    void resolveConversationDestination(
      { forumId, record, recordId, readerId: session.user?.id },
      readersFor(dispatch, session),
    ).then((destination) => {
      // A reader who has gone elsewhere, or followed another link here, is not sent anywhere.
      if (mounted.current && startedRequest.current === request) navigate(destination, { replace: true });
    });
  }, [dispatch, forumId, navigate, record, recordId, session]);

  return (
    <Waiting>
      <Loader text="Opening the conversation..." />
    </Waiting>
  );
}
